import {
  instantToZonedInput,
  SUPPORTED_LOCALES,
  zonedInputToInstant,
  type Locale,
} from "@festipal/shared";
import { useCallback, useEffect, useState } from "react";

import { festivals, type PublicationState } from "./api";

/**
 * Édition d'un festival.
 *
 * Deux réglages y sont plus lourds de conséquences qu'ils n'en ont l'air, et
 * l'interface le dit : le fuseau et l'heure de bascule déplacent toute la
 * grille, puisqu'ils décident à quelle journée appartient un concert de fin de
 * nuit.
 */

type Props = { slug: string };

type Draft = {
  timezone: string;
  festivalDayStartHour: number;
  startsAt: string;
  endsAt: string;
  translations: Partial<Record<Locale, { name: string; description?: string }>>;
};

type BundleShape = {
  festival: {
    timezone: string;
    festivalDayStartHour: number;
    startsAt: string;
    endsAt: string;
    name: Record<string, string>;
    description: Record<string, string>;
  };
  pois: unknown[];
  performances: unknown[];
};

export function FestivalEditor({ slug }: Props) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [publication, setPublication] = useState<PublicationState | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const bundle = (await festivals.bundle(slug)) as BundleShape;
    setDraft({
      timezone: bundle.festival.timezone,
      festivalDayStartHour: bundle.festival.festivalDayStartHour,
      // Shown in the site's time. Reading the UTC string as local time, then
      // saving it in the browser's zone, moved the festival two hours earlier
      // at every save until 2026-10-13.
      startsAt: instantToZonedInput(
        new Date(bundle.festival.startsAt),
        bundle.festival.timezone,
      ),
      endsAt: instantToZonedInput(
        new Date(bundle.festival.endsAt),
        bundle.festival.timezone,
      ),
      translations: Object.fromEntries(
        SUPPORTED_LOCALES.map((locale) => [
          locale,
          {
            name: bundle.festival.name[locale] ?? "",
            description: bundle.festival.description[locale] ?? "",
          },
        ]),
      ),
    });
    setPublication(await festivals.publication(slug).catch(() => null));
  }, [slug]);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (!draft) return <p className="muted">Chargement…</p>;

  const save = async () => {
    let startsAt: Date | null = null;
    let endsAt: Date | null = null;
    try {
      startsAt = zonedInputToInstant(draft.startsAt, draft.timezone);
      endsAt = zonedInputToInstant(draft.endsAt, draft.timezone);
    } catch {
      // An unknown time zone name: Intl refuses it.
    }
    if (!startsAt || !endsAt) {
      setMessage("Renseignez les dates de début et de fin, et un fuseau valable.");
      return;
    }

    setBusy(true);
    setMessage(null);
    try {
      await festivals.update(slug, {
        timezone: draft.timezone,
        festivalDayStartHour: draft.festivalDayStartHour,
        startsAt: startsAt.toISOString(),
        endsAt: endsAt.toISOString(),
        translations: Object.fromEntries(
          Object.entries(draft.translations)
            .filter(([, value]) => value?.name)
            .map(([locale, value]) => [
              locale,
              { name: value!.name, description: value!.description || undefined },
            ]),
        ),
      });
      setMessage("Enregistré. Publiez pour que les festivaliers le voient.");
      await reload();
    } catch {
      setMessage("Enregistrement impossible.");
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const result = await festivals.publish(slug);
      setMessage(
        `Publié : ${result.pois} ${result.pois > 1 ? "lieux" : "lieu"}, ${result.performances} ${result.performances > 1 ? "concerts" : "concert"} envoyés aux applications.`,
      );
      await reload();
    } catch {
      setMessage("Publication impossible.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="card">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h2>Publication</h2>
          {publication?.hasUnpublishedChanges ? (
            <span className="badge pending">Modifications non publiées</span>
          ) : (
            <span className="badge published">À jour</span>
          )}
        </div>

        <p className="muted">
          Ce que vous saisissez n'atteint les téléphones qu'à la publication. Vous
          pouvez donc corriger tranquillement avant d'envoyer.
        </p>

        <div className="row">
          <button
            className="primary"
            onClick={() => void publish()}
            disabled={busy || !publication?.hasUnpublishedChanges}
          >
            Publier
          </button>
          {publication?.publishedAt ? (
            <span className="muted">
              Dernière publication :{" "}
              {new Date(publication.publishedAt).toLocaleString("fr-FR")}
            </span>
          ) : (
            <span className="muted">Jamais publié</span>
          )}
        </div>

        {message ? <p className="muted">{message}</p> : null}
      </section>

      <section className="card">
        <h2>Dates et fuseau</h2>

        <div className="grid-2">
          <label>
            Début
            <input
              type="datetime-local"
              value={draft.startsAt}
              onChange={(e) => setDraft({ ...draft, startsAt: e.target.value })}
            />
          </label>
          <label>
            Fin
            <input
              type="datetime-local"
              value={draft.endsAt}
              onChange={(e) => setDraft({ ...draft, endsAt: e.target.value })}
            />
          </label>
          <label>
            Fuseau horaire du site
            <input
              value={draft.timezone}
              onChange={(e) => setDraft({ ...draft, timezone: e.target.value })}
              placeholder="Europe/Paris"
            />
          </label>
          <label>
            La journée bascule à
            <select
              value={draft.festivalDayStartHour}
              onChange={(e) =>
                setDraft({ ...draft, festivalDayStartHour: Number(e.target.value) })
              }
            >
              {Array.from({ length: 24 }, (_, hour) => (
                <option key={hour} value={hour}>
                  {String(hour).padStart(2, "0")} h
                </option>
              ))}
            </select>
          </label>
        </div>

        <p className="muted">
          Un concert qui commence avant cette heure appartient à la soirée de la veille.
          Avec 06 h, un set de 01 h du matin reste rattaché au vendredi. Changer ce
          réglage redistribue toute la programmation.
        </p>
      </section>

      <section className="card">
        <h2>Nom et description</h2>
        <p className="muted">
          Le français fait référence. Une langue laissée vide se replie dessus dans
          l'application.
        </p>

        {SUPPORTED_LOCALES.map((locale) => (
          <div key={locale} className="grid-2">
            <label>
              Nom ({locale})
              <input
                value={draft.translations[locale]?.name ?? ""}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    translations: {
                      ...draft.translations,
                      [locale]: {
                        ...draft.translations[locale],
                        name: e.target.value,
                      },
                    },
                  })
                }
              />
            </label>
            <label>
              Description ({locale})
              <input
                value={draft.translations[locale]?.description ?? ""}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    translations: {
                      ...draft.translations,
                      [locale]: {
                        name: draft.translations[locale]?.name ?? "",
                        description: e.target.value,
                      },
                    },
                  })
                }
              />
            </label>
          </div>
        ))}

        <div className="row">
          <button className="primary" onClick={() => void save()} disabled={busy}>
            Enregistrer
          </button>
        </div>
      </section>
    </>
  );
}
